import type { StatusTone } from '@beonit/adaptive-raindrop';
import type { LocationQueryRaw } from 'vue-router';
import type {
  GestureLabelScope,
  VideoGestureLabels,
  VideoLabel,
  VideoLabelFilter,
  VideoLabelling,
} from '../model.js';
import { incoherenceRangeText } from './incoherence-range.js';

/**
 * A video's truth label, as words and colours (HU-36).
 *
 * On screen it is «Verdad» or «Mentira» again (product owner, 2026-10-20), over the same stored
 * states: `incoherent` — at least one «tramo de mentira» — is «Mentira», `coherent` — complete with
 * no tramo — is «Verdad». «Sin etiquetar» is a state with a consequence — the video counts in no
 * metric — and never «Verdad» by default. Red the lie, green the truth, neutral nobody has said.
 */

export const LABEL_TEXT: Readonly<Record<VideoLabel | 'unlabelled', string>> = Object.freeze({
  incoherent: 'Mentira',
  coherent: 'Verdad',
  unlabelled: 'Sin etiquetar',
});

export const LABEL_TONE: Readonly<Record<VideoLabel | 'unlabelled', StatusTone>> = Object.freeze({
  incoherent: 'danger',
  coherent: 'success',
  unlabelled: 'neutral',
});

/** The gallery's label filter, in the order the badges are read. */
export const LABEL_FILTER_OPTIONS: ReadonlyArray<{ value: VideoLabelFilter; label: string }> =
  Object.freeze([
    { value: 'incoherent', label: LABEL_TEXT.incoherent },
    { value: 'coherent', label: LABEL_TEXT.coherent },
    { value: 'unlabelled', label: LABEL_TEXT.unlabelled },
  ]);

/**
 * A label as upstream sent it, or null.
 *
 * The rows of the dataset speak the states. The group members and an execution's videos are passed
 * through from Virtual Profiler, which may still spell them as the single label did — `incoherence`
 * a lie, `truthful` a video with none — so both spellings are read; anything else is no label.
 */
export function labelStateOf(raw: unknown): VideoLabel | null {
  switch (raw) {
    case 'incoherent':
    case 'incoherence':
      return 'incoherent';
    case 'coherent':
    case 'truthful':
      return 'coherent';
    default:
      return null;
  }
}

export interface LabelBadge {
  text: string;
  tone: StatusTone;
}

/** The chip a card or a row shows for a video's label. */
export function labelBadge(label: VideoLabel | null): LabelBadge {
  const key = label ?? 'unlabelled';

  return { text: LABEL_TEXT[key], tone: LABEL_TONE[key] };
}

export interface LabellingStatus extends LabelBadge {
  /** The lie's tramos, for the chip's tooltip; null when there is none. */
  detail: string | null;
}

/** `undefined` is the labelling still being read, which is no chip rather than «Sin etiquetar». */
export function labellingStatus(
  labelling: VideoLabelling | null | undefined,
): LabellingStatus | null {
  if (labelling === undefined) return null;

  const state = labelling?.state ?? 'unlabelled';
  const badge = { text: LABEL_TEXT[state], tone: LABEL_TONE[state] };

  if (!labelling || labelling.ranges.length === 0) return { ...badge, detail: null };

  const ranges = labelling.ranges.map((range) => incoherenceRangeText(range)).join(', ');

  return { ...badge, detail: `${rangeCount(labelling.ranges.length)}: ${ranges}` };
}

/** «1 tramo», «3 tramos»: the tramos of a lie. */
export function rangeCount(count: number): string {
  return `${count} ${count === 1 ? 'tramo' : 'tramos'}`;
}

/**
 * What «Etiquetar» labels on the video detail, one at a time (product owner, 2026-10-29).
 *
 * A video has three labellings, each its own document with its own «complete»: its gestures
 * (`gestures`), only its alerts (`gesture_alerts`, what Alertas gestuales measures against) and its
 * veracity (`truth`: Verdad or Mentira with its tramos). The review labels the one `?scope=` names,
 * the gestures unless told, and the header says where the three stand.
 */
export const LABEL_SCOPES = ['gestures', 'gesture_alerts', 'truth'] as const;

export type LabelScope = (typeof LABEL_SCOPES)[number];

/** The selector's words. */
export const LABEL_SCOPE_LABELS: Readonly<Record<LabelScope, string>> = Object.freeze({
  gestures: 'Gestos',
  gesture_alerts: 'Alertas gestuales',
  truth: 'Veracidad',
});

/** The gestures unless the address names another; a mistyped one is the gestures too. */
export function labelScopeFrom(raw: unknown): LabelScope {
  return typeof raw === 'string' && (LABEL_SCOPES as readonly string[]).includes(raw)
    ? (raw as LabelScope)
    : 'gestures';
}

/** The address in «Etiquetar» on one scope; the default scope is left out, as `?mode=label` alone. */
export function withLabelScope(query: Readonly<LocationQueryRaw>, scope: LabelScope): LocationQueryRaw {
  return { ...query, mode: 'label', scope: scope === 'gestures' ? undefined : scope };
}

/** The chip's noun for a gesture labelling: short, the header has three of them. */
const GESTURE_SCOPE_NOUN: Readonly<Record<GestureLabelScope, string>> = Object.freeze({
  gestures: 'Gestos',
  gesture_alerts: 'Alertas',
});

/**
 * The header's chip for one gesture labelling: «Gestos: completo», «Alertas: 3 etiquetas · sin
 * completar», «Gestos: sin etiquetar». Only the mark set by hand says complete, as in the control's
 * list: labels alone are a labelling nobody has finished. `undefined` is the document still being
 * read, which is no chip.
 */
export function gestureLabellingStatus(
  doc: VideoGestureLabels | undefined,
  scope: GestureLabelScope,
): LabellingStatus | null {
  if (doc === undefined) return null;

  const noun = GESTURE_SCOPE_NOUN[scope];
  const count = doc.labels.length;
  const labels = `${count} ${count === 1 ? 'etiqueta' : 'etiquetas'}`;

  if (doc.complete) {
    const by = doc.completedBy ? `por ${doc.completedBy}` : null;

    return {
      text: `${noun}: completo`,
      tone: 'success',
      detail: [labels, by].filter((part) => part !== null).join(' · '),
    };
  }
  if (count > 0) return { text: `${noun}: ${labels} · sin completar`, tone: 'warning', detail: null };

  return { text: `${noun}: sin etiquetar`, tone: 'neutral', detail: null };
}
